import {
  useCallback,
  useState,
  createContext,
  useContext,
  useEffect,
} from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { RankPeriodTab, RankTab, useRank } from "./use-rank";
import { getISOWeek, getISOWeekYear } from "date-fns";
import { GetRankResponse, RankDto } from "../../../../api/member";
import { Cafe } from "../../../../api/types";
import {
  rankingCacheState,
  RankingCacheKey,
  createCacheKey,
  CachedRankingData,
} from "../../../../recoil/rank";
import { TournamentSeasonDto } from "../../../../api/ranking-season";
import {
  getMemberMonthRanking,
  getMemberRanking,
  getMemberWeekRanking,
} from "../../../../api/member-ranking";
import {
  getRunnerMonthRanking,
  getRunnerRanking,
  getRunnerWeekRanking,
} from "../../../../api/runner-ranking";
import useGeoLocation from "../../../../hooks/useGeoLocation";
import { getFranchisePub } from "../../../../api/cafe";
import { userState } from "../../../../recoil/auth";
import { getISOWeekYearAndWeek } from "../components/filter-tab/utils/date";

export interface useRankingProps {
  pubFilter: Cafe | null;
  pubSeason: number;
  companySeason: number;
  year: number;
  month: number;
  week: number;
}

interface RankContextType extends ReturnType<typeof useRank> {
  // 랭킹 리스트
  getList: () => void;
  myRank: RankDto | null;
  list: RankDto[];
  isLoading: boolean;
  isInitialDataLoaded: boolean;
  setIsInitialDataLoaded: (v: boolean) => void;
  currentPage: number;
  setCurrentPage: (v: number) => void;
  total: number;
  hasMore: boolean;
  loadMoreData: (page: number, isInitial?: boolean) => void;
  // 랭킹 필터 정보
  year: number;
  setYear: (v: number) => void;
  weekYear: number;
  setWeekYear: (v: number) => void;
  month: number;
  setMonth: (v: number) => void;
  week: number;
  setWeek: (v: number) => void;
  pubFilter: Cafe | null;
  setPubFilter: (v: Cafe | null) => void;
  pubSeason: TournamentSeasonDto | undefined;
  setPubSeason: (v: TournamentSeasonDto) => void;
  // 펍 필터 | 랭킹 안내
  isOpenPubFilter: boolean;
  setIsOpenPubFilter: (v: boolean) => void;
  isOpenRankInfo: { open: boolean; isButton: boolean };
  setIsOpenRankInfo: (v: { open: boolean; isButton: boolean }) => void;
  // 해더 표시 여부
  showHeader: boolean;
  setShowHeader: (v: boolean) => void;
  // 펍 필터 리스트
  pubFilterList: Cafe[];
}

const RankContext = createContext<RankContextType | null>(null);

export const RankProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const user = useRecoilValue(userState);

  // 탭
  const {
    mainTab,
    setMainTab,
    isMainTab,
    periodTab,
    setPeriodTab,
    isPeriodTab,
  } = useRank();

  // 랭킹 스크롤 페이징
  const limit = 20;
  const [list, setList] = useState<RankDto[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // 페이징 시 불러올 데이터가 더 존재하는지 여부
  const [hasMore, setHasMore] = useState<boolean>(true);
  // api 호출 진행중 여부
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // 초기 데이터 로드 완료 여부
  const [isInitialDataLoaded, setIsInitialDataLoaded] =
    useState<boolean>(false);

  // 각 캐시키가 가지고 있는 랭킹 데이터
  const [rankingCache, setRankingCache] = useRecoilState(rankingCacheState);

  // 유저 랭킹
  const [myRank, setMyRank] = useState<RankDto | null>(null);

  // 리스트 조회 필터
  const currentDate = new Date();
  const { weekYear: currentWeekYear, week: currenWeek } =
    getISOWeekYearAndWeek(currentDate);
  const [year, setYear] = useState(currentDate.getFullYear());
  const [weekYear, setWeekYear] = useState(currentWeekYear);
  const [week, setWeek] = useState(currenWeek);
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [pubSeason, setPubSeason] = useState<TournamentSeasonDto>();
  const [pubFilter, setPubFilter] = useState<Cafe | null>(null);

  // 팝업, 필터 오픈 여부, 해더 노출 여부
  const [isOpenPubFilter, setIsOpenPubFilter] = useState<boolean>(false);
  const [isOpenRankInfo, setIsOpenRankInfo] = useState<{
    open: boolean;
    isButton: boolean;
  }>({ open: false, isButton: false });
  const [showHeader, setShowHeader] = useState<boolean>(true);

  // 펍 필터 리스트
  const { latitude, longitude } = useGeoLocation();
  const [pubFilterList, setPubFilterList] = useState<Cafe[]>([]);

  // 필터 변환 시 캐시 키 생성(조회)
  const getNewCacheKey = useCallback((): string => {
    const params: RankingCacheKey = {
      type: mainTab,
      pubFilterId: pubFilter ? pubFilter.id : -1,
      pubSeasonNum: pubSeason?.seasonNumber || 0,
      companySeasonNum: 0,
      year,
      month,
      week,
      filterType: periodTab,
      userId: user?.id || 0,
    };
    return createCacheKey(params);
  }, [pubFilter, pubSeason, year, month, week, mainTab, periodTab]);

  // 캐시키에 맞는 데이터 가져오기
  const getCachedData = useCallback(
    (cacheKey: string): CachedRankingData | null => {
      return rankingCache[cacheKey] || null;
    },
    [rankingCache]
  );

  // 해당 캐시키에 데이터 추가 저장하기
  const setCachedData = useCallback(
    (cacheKey: string, data: CachedRankingData) => {
      setRankingCache((prev) => ({
        ...prev,
        [cacheKey]: data,
      }));
    },
    [setRankingCache]
  );

  // 랭킹 리스트 api
  const rankingApi = async (page: number) => {
    // 시즌 탭인 경우에만 시즌 정보가 필요함
    if (isPeriodTab(RankPeriodTab.SEASON) && !pubSeason) {
      return null;
    }

    if (!pubFilter) {
      return null;
    }

    if (isMainTab(RankTab.PUB) && pubFilter) {
      return isPeriodTab(RankPeriodTab.MONTH)
        ? await getMemberMonthRanking(pubFilter.id, year, month, limit, page)
        : isPeriodTab(RankPeriodTab.WEEK)
        ? await getMemberWeekRanking(pubFilter.id, weekYear, week, limit, page)
        : await getMemberRanking(
            pubSeason?.seasonNumber || 0,
            pubFilter.id,
            limit,
            page
          );
    }
    return isPeriodTab(RankPeriodTab.MONTH)
      ? await getRunnerMonthRanking(limit, page, year, month)
      : isPeriodTab(RankPeriodTab.WEEK)
      ? await getRunnerWeekRanking(limit, page, weekYear, week)
      : await getRunnerRanking(pubSeason?.seasonNumber || 0, limit, page);
  };

  // 초기 값 설정 (캐시키 생성, 데이터 설정)
  const setInitial = (res: GetRankResponse, cacheKey: string) => {
    const newData: CachedRankingData = {
      rankings: res.rankings,
      myRank: res.myRank,
      total: res.total,
      lastUpdated: Date.now(),
    };

    setList(res.rankings);
    setTotal(res.total);
    setMyRank(res.myRank);
    setHasMore(res.rankings.length < res.total);
    setCachedData(cacheKey, newData);
  };

  const loadMoreData = useCallback(
    async (page: number, isInitial: boolean = false) => {
      if (isLoading || (!true && !isInitial)) return;

      const cacheKey = getNewCacheKey();

      setIsLoading(true);
      try {
        const res = await rankingApi(page);
        if (!res) {
          // 시즌 데이터가 없는 경우 빈 데이터로 초기화
          if (isInitial) {
            setList([]);
            setTotal(0);
            setMyRank(null);
            setHasMore(false);
          }
          return;
        }

        if (isInitial) {
          // 초기값 설정
          setInitial(res, cacheKey);
          setIsInitialDataLoaded(true);
        } else {
          // 추가 데이터 로드 시 기존 리스트에 추가
          setList((prevList) => {
            const newList = [...prevList, ...res.rankings];
            const uniqueList = newList.filter(
              (item, index, self) =>
                index === self.findIndex((t) => t.rank === item.rank)
            );

            // 캐시 업데이트 (추가 데이터 포함)
            const updatedData: CachedRankingData = {
              rankings: uniqueList,
              myRank: res.myRank,
              total: res.total,
              lastUpdated: Date.now(),
            };
            setCachedData(cacheKey, updatedData);

            return uniqueList;
          });

          // 더 불러올 데이터가 있는지 확인
          setHasMore(page * limit < res.total);
        }
      } catch (e) {
        console.error(e);
        if (isInitial) {
          setList([]);
          setHasMore(false);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      isLoading,
      getNewCacheKey,
      setCachedData,
      pubSeason,
      year,
      month,
      week,
      mainTab,
      periodTab,
      pubFilter,
    ]
  );

  // 랭킹 리스트 조회 (필터 등 변경 시 최초 호출)
  const getList = async () => {
    // 해당 조회 조건의 캐시키 생성
    const cacheKey = getNewCacheKey();
    // 해당 캐시키 데이터 조회
    const cachedData = getCachedData(cacheKey);

    // 캐시키 데이터 존재 시 데이터 설정
    if (cachedData) {
      setList(cachedData.rankings);
      setMyRank(cachedData.myRank);
      setTotal(cachedData.total);
      setHasMore(cachedData.rankings.length < cachedData.total);
      setIsInitialDataLoaded(true);
      return;
    } else {
      setHasMore(true);
      setList([]);
      setIsInitialDataLoaded(false);
    }

    // 데이터 호출
    await loadMoreData(1, true);
  };

  // 시즌 정보가 로드된 후 초기 데이터 로드
  useEffect(() => {
    if (currentPage === 1 && pubSeason && !isInitialDataLoaded && pubFilter) {
      getList();
    }
  }, [currentPage, pubSeason, isInitialDataLoaded, pubFilter]);

  // 펍 리스트
  const getPubFilterList = async () => {
    const res = await getFranchisePub(latitude, longitude);
    const result = res.list.filter((item) => item.pubType !== "RUNNER");
    setPubFilterList(result);
    setPubFilter(result[0]);
    if (user?.ranking) {
      const pub = result.find(
        (item) => item.cafeName === user.ranking?.pubName
      );
      if (pub) {
        setPubFilter(pub);
      }
    }
  };

  useEffect(() => {
    getPubFilterList();
  }, []);

  const value = {
    // 랭킹 리스트
    getList,
    myRank,
    list,
    isLoading,
    isInitialDataLoaded,
    setIsInitialDataLoaded,
    currentPage,
    setCurrentPage,
    total,
    hasMore,
    loadMoreData,
    // 탭 정보
    mainTab,
    setMainTab,
    isMainTab,
    periodTab,
    setPeriodTab,
    isPeriodTab,
    // 랭킹 필터 정보
    year,
    setYear,
    weekYear,
    setWeekYear,
    month,
    setMonth,
    week,
    setWeek,
    pubFilter,
    setPubFilter,
    pubSeason,
    setPubSeason,
    // 펍 필터 | 랭킹 안내 오픈 여부
    isOpenPubFilter,
    setIsOpenPubFilter,
    isOpenRankInfo,
    setIsOpenRankInfo,
    // 해더 투명 여부
    showHeader,
    setShowHeader,
    // 펍 필터 리스트
    pubFilterList,
  };

  return <RankContext.Provider value={value}>{children}</RankContext.Provider>;
};

export const useRankContext = () => {
  const context = useContext(RankContext);
  if (!context) {
    throw new Error("useContext must be used within a GameProvider");
  }
  return context;
};
